/**
 * Grab targets for the lavar and the aggregat, in the 3D scene.
 *
 * A separate R3F component rather than an addition to `createInteractionOverlays`
 * for two reasons. It is CONDITIONAL — interior handles exist only while interior
 * editing is on, and mounting/unmounting a component is a cheaper way to express
 * that than rebuilding the whole interaction group on a flag. And it is
 * declarative: `ViewerMeasurementController.getMeasurementObjects` traverses the
 * scene for `userData.measurementTarget`, so a handle is picked up wherever it
 * hangs.
 *
 * Deliberately a pure map: every descriptor already carries its finished `userData`
 * (target, pick layer, cursor, opacity triple) from `createInteriorHandles`. This
 * component contributes only the material, so an unrelated `SceneCanvas` re-render —
 * `cameraIdle` flipping on every orbit, a model revision — cannot re-allocate a
 * measurement target per handle.
 *
 * Lifecycle, which is the fiddly part:
 *
 *  - GEOMETRY is the shared `UNIT_BOX` plus a scale, which is what
 *    `engines/geometry.ts` exists for: O(1) buffers instead of one `BoxGeometry` per
 *    handle, and the same buffer the drag ghost uses. It must never be disposed.
 *  - MATERIAL has to be per-handle, because hover mutates `material.opacity` in
 *    place (`getMaterialOpacity`) — one shared instance would light up every bench
 *    when you hovered any of them.
 *
 * Those two pull in opposite directions, and `dispose={null}` propagates down the
 * subtree, so it cannot free the material without also exposing the geometry. The
 * materials are therefore owned explicitly here and disposed in an effect.
 *
 * `dispose={null}` is now belt-and-braces rather than the only thing protecting the
 * shared buffer: `UNIT_BOX` is created through `shareForProcessLifetime`, so its
 * `dispose` is a no-op and no consumer — this one, R3F, or a future hand-written
 * teardown — can free it. See `src/engines/sharedGeometry.test.ts`.
 */
import { Html } from "@react-three/drei";
import { memo, useEffect, useMemo } from "react";
import * as THREE from "three";
import { UNIT_BOX } from "../../engines/geometry";
import type { InteriorEditSetup } from "./interiorHandles";

/** Terracotta — the repo's established "you can grab this" accent. */
const BENCH_HANDLE_COLOR = "#e8873c";
/** Amber, matching the door pill's family and reading as heat. */
const HEATER_HANDLE_COLOR = "#f59e0b";

interface InteriorEditHandlesProps {
	/** Null when the mode is off or the design has no layout-driven interior. */
	setup: InteriorEditSetup | null;
}

export const InteriorEditHandles = memo(function InteriorEditHandles({
	setup,
}: InteriorEditHandlesProps) {
	// One material per handle, rebuilt only when the handle set itself changes.
	const materials = useMemo(
		() =>
			(setup?.handles ?? []).map(
				(handle) =>
					new THREE.MeshBasicMaterial({
						color:
							handle.userData.measurementTarget.kind === "heater"
								? HEATER_HANDLE_COLOR
								: BENCH_HANDLE_COLOR,
						transparent: true,
						opacity: handle.userData.baseOpacity,
						depthWrite: false,
						// The pick box shares the bench's outer faces. Bias only its
						// rendered depth so the tint cannot fight the slats, while real
						// foreground objects still occlude it and picking stays exact.
						polygonOffset: true,
						polygonOffsetFactor: -1,
						polygonOffsetUnits: -1,
						toneMapped: false,
						side: THREE.FrontSide,
					}),
			),
		[setup?.handles],
	);

	useEffect(
		() => () => {
			for (const material of materials) material.dispose();
		},
		[materials],
	);

	if (!setup) return null;

	return (
		<group name="detail/interaction/interior">
			{setup.handles.map((handle, index) => (
				<mesh
					key={handle.id}
					name={`detail/interaction/interior/${handle.id}`}
					geometry={UNIT_BOX}
					material={materials[index]}
					position={handle.box.centerM}
					scale={handle.box.sizeM}
					userData={handle.userData}
					// Keeps R3F away from BOTH the shared geometry and the material — the
					// effect above owns the material's lifetime.
					dispose={null}
				>
					{handle.userData.measurementTarget.kind === "bench" &&
						handle.userData.measurementTarget.planTarget.kind ===
							"bench-height" && (
							<Html
								center
								zIndexRange={[1, 0]}
								style={{
									pointerEvents: "none",
									color: "white",
									fontSize: "18px",
									fontWeight: 700,
								}}
							>
								<span aria-hidden="true">↕</span>
							</Html>
						)}
				</mesh>
			))}
		</group>
	);
});
